import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Package, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, token, isAuthenticated, logout } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${API}/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(res.data);
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [isAuthenticated, navigate, token]);

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30',
      confirmed: 'bg-blue-500/20 text-blue-500 border-blue-500/30',
      processing: 'bg-purple-500/20 text-purple-500 border-purple-500/30',
      shipped: 'bg-cyan-500/20 text-cyan-500 border-cyan-500/30',
      delivered: 'bg-green-500/20 text-green-500 border-green-500/30',
      cancelled: 'bg-red-500/20 text-red-500 border-red-500/30',
    };
    return colors[status] || 'bg-white/10 text-white/50';
  };

  const getStatusText = (status) => {
    const texts = {
      pending: 'ÎN AȘTEPTARE',
      confirmed: 'CONFIRMAT',
      processing: 'ÎN PROCESARE',
      shipped: 'EXPEDIAT',
      delivered: 'LIVRAT',
      cancelled: 'ANULAT',
    };
    return texts[status] || status.toUpperCase();
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-500',
      paid: 'bg-green-500/20 text-green-500',
      cod_pending: 'bg-blue-500/20 text-blue-500',
      failed: 'bg-red-500/20 text-red-500',
    };
    return colors[status] || 'bg-white/10 text-white/50';
  };

  const getPaymentStatusText = (status) => {
    const texts = {
      pending: 'ÎN AȘTEPTARE',
      paid: 'PLĂTIT',
      cod_pending: 'RAMBURS',
      failed: 'EȘUAT',
    };
    return texts[status] || status.toUpperCase();
  };

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="px-6 md:px-12 lg:px-24 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-12">
            <div>
              <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight uppercase text-white" data-testid="profile-title">
                CONTUL MEU
              </h1>
              <p className="font-body text-white/50 mt-2">{user?.email}</p>
            </div>
            <button
              onClick={logout}
              className="font-body text-sm text-white/50 hover:text-[#c00000] transition-colors"
              data-testid="logout-btn"
            >
              DECONECTARE
            </button>
          </div>

          {/* User Info */}
          <div className="bg-[#121212] border border-white/5 p-8 mb-12">
            <h2 className="font-heading font-bold text-xl tracking-wider text-white mb-6">PROFIL</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <span className="font-body text-xs text-white/50 uppercase tracking-wider">Nume</span>
                <p className="font-body text-white mt-1">{user?.name}</p>
              </div>
              <div>
                <span className="font-body text-xs text-white/50 uppercase tracking-wider">Email</span>
                <p className="font-body text-white mt-1">{user?.email}</p>
              </div>
            </div>
          </div>

          {/* Orders */}
          <div>
            <h2 className="font-heading font-bold text-2xl tracking-wider text-white mb-8">COMENZILE MELE</h2>
            
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="bg-[#121212] border border-white/5 p-6 animate-pulse">
                    <div className="h-4 bg-white/5 w-1/4 mb-4" />
                    <div className="h-4 bg-white/5 w-1/2" />
                  </div>
                ))}
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-[#121212] border border-white/5 p-12 text-center">
                <Package className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <h3 className="font-heading text-xl text-white mb-2">Nicio comandă încă</h3>
                <p className="font-body text-white/50">Începe cumpărăturile pentru a vedea comenzile aici</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order, idx) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    className="bg-[#121212] border border-white/5 p-6"
                    data-testid={`order-${idx}`}
                  >
                    <div className="flex flex-col md:flex-row justify-between gap-4 mb-4">
                      <div>
                        <span className="font-body text-xs text-white/50">Nr. Comandă</span>
                        <p className="font-body text-sm text-white">{order.id.slice(0, 8)}...</p>
                      </div>
                      <div>
                        <span className="font-body text-xs text-white/50">Data</span>
                        <p className="font-body text-sm text-white">
                          {new Date(order.created_at).toLocaleDateString('ro-RO')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={`${getStatusColor(order.order_status)} rounded-none font-heading text-xs`}>
                          {getStatusText(order.order_status)}
                        </Badge>
                        <Badge className={`${getPaymentStatusColor(order.payment_status)} rounded-none font-heading text-xs`}>
                          {getPaymentStatusText(order.payment_status)}
                        </Badge>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 mb-4">
                      {order.items.map((item, i) => (
                        <div key={i} className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-[#0a0a0a]">
                            <img src={item.product_image} alt="" className="w-full h-full object-cover" />
                          </div>
                          <div>
                            <p className="font-body text-xs text-white">{item.product_name}</p>
                            <p className="font-body text-xs text-white/50">Mărime {item.size} × {item.quantity}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-between items-center pt-4 border-t border-white/5">
                      <div>
                        <span className="font-body text-xs text-white/50">Livrare la</span>
                        <p className="font-body text-sm text-white">{order.address.city}, {order.address.county}</p>
                      </div>
                      <div className="text-right">
                        <span className="font-body text-xs text-white/50">Total</span>
                        <p className="font-heading font-bold text-lg text-[#c00000]">{order.total.toFixed(2)} RON</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
